import { Injectable, computed, inject, linkedSignal } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@env';
import { WINDOW } from '@tvd/cdk/browser';
import { AuthService } from './auth.service';

export type UserMode = 'staff' | 'customer' | 'all';

const USER_MODE_KEY = 'zyn_user_mode';

@Injectable({
  providedIn: 'root',
})
export class UserModeService {
  readonly #auth = inject(AuthService);
  readonly #winRef = inject(WINDOW);
  readonly #router = inject(Router);

  readonly #user = computed(() => {
    if (this.#auth.user.hasValue()) {
      return this.#auth.user.value();
    }
    return null;
  });

  // ADR 0008: two facets. `roles` is staff-only; `customerOf` is the customer
  // relationship. Mode is derived from which facets are present, not from a
  // 'customer' string inside `roles`.
  readonly #isStaff = computed(() => (this.#user()?.roles?.length ?? 0) > 0);
  readonly #isCustomer = computed(
    () => (this.#user()?.customerOf?.length ?? 0) > 0,
  );

  readonly userMode = linkedSignal<UserMode | null>(() => {
    const modeFromStorage = this.#winRef.localStorage.getItem(
      USER_MODE_KEY,
    ) as UserMode | null;
    const isStaff = this.#isStaff();
    const isCustomer = this.#isCustomer();

    // Neither facet yet (e.g. mid-registration): null-ish default, not broken.
    if (!isStaff && !isCustomer) return null;

    let mode: UserMode;
    if (isStaff && isCustomer) {
      mode = 'all';
    } else if (isCustomer) {
      mode = 'customer';
    } else {
      mode = 'staff';
    }

    if (!modeFromStorage) {
      return mode;
    }
    if (mode === 'all') {
      // Stored 'customer' is a Booking-Wizard preference; honouring it here
      // would 404 a dual member out of the dashboard via userModeGuard(['staff','all']).
      return modeFromStorage === 'customer' ? 'all' : modeFromStorage;
    }
    this.#winRef.localStorage.setItem(USER_MODE_KEY, mode);
    return mode;
  });

  // Toggle is for dual members only — both a staff member and a customer.
  readonly hasModes = computed(() => this.#isStaff() && this.#isCustomer());

  toggleMode() {
    if (!this.hasModes()) return;

    const mode = this.userMode();
    const next: UserMode = mode === 'customer' ? 'staff' : 'customer';
    this.userMode.set(next);
    this.#winRef.localStorage.setItem(USER_MODE_KEY, next);

    // Customer bookings live in the separate Booking Wizard app, not the
    // dashboard (which gates customer-mode users out). Staff mode stays here.
    if (next === 'customer') {
      this.#winRef.location.href = environment.bookingWizardUrl;
      return;
    }
    this.#router.navigate(['/']);
  }
}
